// Service for sesjonsdata (data som kun skal vare for denne browser-sesjonen)
// Uses fallback: sessionStorage first, then localStorage for better compatibility
// Includes TTL/expiry validation to prevent use of stale data

// TTL constants (in milliseconds)
const DEVICE_UUID_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours for device UUID
const REDIRECT_TRACKING_TTL_MS = 5 * 60 * 1000; // 5 minutes for redirect tracking

// Helper to get TTL for a specific key
function getTTLForKey(key) {
    if (key === 'current_device_uuid') {
        return DEVICE_UUID_TTL_MS;
    } else if (key === 'vipps_redirect_time' || key === 'vipps_redirect_browser') {
        return REDIRECT_TRACKING_TTL_MS;
    }
    return null; // No TTL for unknown keys
}

// Helper to check if data is expired
function isDataExpired(createdAt, ttlMs) {
    if (!createdAt || !ttlMs) return false;
    const now = Date.now();
    const age = now - createdAt;
    return age > ttlMs;
}

// Helper functions with localStorage fallback and TTL validation
function getStorageItem(key) {
    let rawValue = null;
    
    try {
        // Try sessionStorage first
        rawValue = window.sessionStorage.getItem(key);
    } catch (error) {
        // Log specific error type for debugging
        if (error && error.name === 'QuotaExceededError') {
            console.warn('SessionStorage quota exceeded for', key, '- falling back to localStorage');
        } else if (error && error.name === 'SecurityError') {
            console.warn('SessionStorage security error for', key, '(possibly private mode) - falling back to localStorage');
        } else if (error) {
            console.warn('SessionStorage error for', key, ':', error.name || error.message, '- falling back to localStorage');
        }
        
        // Fallback to localStorage if sessionStorage fails
        try {
            rawValue = window.localStorage.getItem(key);
        } catch (localError) {
            if (localError && localError.name === 'QuotaExceededError') {
                console.warn('LocalStorage quota exceeded for', key);
            } else if (localError && localError.name === 'SecurityError') {
                console.warn('LocalStorage security error for', key);
            } else if (localError) {
                console.warn('LocalStorage error for', key, ':', localError.name || localError.message);
            }
            return null;
        }
    }
    
    if (!rawValue) {
        return null;
    }
    
    // Try to parse as JSON (for TTL-enabled data)
    try {
        const parsed = JSON.parse(rawValue);
        // Check if it's a TTL-enabled object
        if (parsed && typeof parsed === 'object' && 'value' in parsed && 'createdAt' in parsed) {
            const ttl = getTTLForKey(key);
            if (ttl && isDataExpired(parsed.createdAt, ttl)) {
                // Data is expired, remove it and return null
                console.warn('Storage data expired for', key, '- age:', Math.round((Date.now() - parsed.createdAt) / 1000 / 60), 'minutes');
                removeStorageItem(key);
                return null;
            }
            // Data is valid, return the value
            return parsed.value;
        }
    } catch (_) {
        // Not JSON, return as-is (backwards compatibility)
    }
    
    // Return raw value if not TTL-enabled
    return rawValue;
}

function setStorageItem(key, value) {
    let sessionSuccess = false;
    let localSuccess = false;
    
    // Check if this key should have TTL
    const ttl = getTTLForKey(key);
    let valueToStore = value;
    
    // If TTL is enabled, wrap value in object with timestamp
    if (ttl) {
        valueToStore = JSON.stringify({
            value: value,
            createdAt: Date.now()
        });
    }
    
    // Try sessionStorage first
    try {
        window.sessionStorage.setItem(key, valueToStore);
        sessionSuccess = true;
    } catch (error) {
        // Log specific error type for debugging
        if (error && error.name === 'QuotaExceededError') {
            console.warn('SessionStorage quota exceeded for', key, '- trying localStorage fallback');
        } else if (error && error.name === 'SecurityError') {
            console.warn('SessionStorage security error for', key, '(possibly private mode) - trying localStorage fallback');
        } else if (error) {
            console.warn('SessionStorage error for', key, ':', error.name || error.message, '- trying localStorage fallback');
        }
        
        // If sessionStorage fails, try localStorage as fallback
        try {
            window.localStorage.setItem(key, valueToStore);
            localSuccess = true;
        } catch (localError) {
            // Both storages failed - log specific error types
            if (localError && localError.name === 'QuotaExceededError') {
                console.warn('Both storages quota exceeded for', key);
            } else if (localError && localError.name === 'SecurityError') {
                console.warn('Both storages security error for', key);
            } else {
                console.warn('Failed to store in both sessionStorage and localStorage:', key, {
                    sessionError: error.name || error.message,
                    localError: localError.name || localError.message
                });
            }
        }
    }
    
    // Return true if at least one storage succeeded
    return sessionSuccess || localSuccess;
}

function removeStorageItem(key) {
    // Remove from both storages to ensure cleanup
    try {
        window.sessionStorage.removeItem(key);
    } catch (_) { /* noop */ }
    
    try {
        window.localStorage.removeItem(key);
    } catch (_) { /* noop */ }
}

export const sessionStorageService = {
    // Device UUID for current session
    getDeviceUuid: () => {
        return getStorageItem('current_device_uuid');
    },
    
    setDeviceUuid: (uuid) => {
        setStorageItem('current_device_uuid', uuid);
    },
    
    removeDeviceUuid: () => {
        removeStorageItem('current_device_uuid');
    },
    
    // Vipps redirect tracking
    getRedirectTime: () => {
        return getStorageItem('vipps_redirect_time');
    },
    
    setRedirectTime: (time) => {
        setStorageItem('vipps_redirect_time', time);
    },
    
    getRedirectBrowser: () => {
        return getStorageItem('vipps_redirect_browser');
    },
    
    setRedirectBrowser: (browser) => {
        setStorageItem('vipps_redirect_browser', browser);
    },
    
    removeRedirectTracking: () => {
        removeStorageItem('vipps_redirect_time');
        removeStorageItem('vipps_redirect_browser');
    }
};

